import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { RefreshCw, TrendingUp, Package, CheckCircle, Clock, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Graph colors matching card text colors exactly (text-X-700)
const DESIGN_COLORS = {
  EMPTY: "#374151",    // gray-700
  PENDING: "#a16207",  // yellow-700
  CHECK: "#1d4ed8",    // blue-700
  READY: "#7e22ce",    // purple-700
  DESIGNED: "#047857", // emerald-700
  CANCEL: "#b91c1c",   // red-700
  ENQUIRE: "#0e7490",  // cyan-700
};

const ORDER_COLORS = {
  PENDING: "#b45309",  // amber-700
  DONE: "#047857",     // emerald-700
  ENQUIRE: "#0e7490",  // cyan-700
  CANCEL: "#b91c1c",   // red-700
  HALF_ID: "#7e22ce",  // purple-700
  FULL_ID: "#6d28d9",  // violet-700
  CHECK: "#1d4ed8",    // blue-700
  HOLD: "#c2410c",     // orange-700
  RTO: "#9d174d",      // pink-800
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [labelCheckData, setLabelCheckData] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkStatusType, setBulkStatusType] = useState('order_status');
  const [bulkResult, setBulkResult] = useState(null);

  const handleBulkUpdate = async () => {
    const rawIds = document.getElementById('bulkOrderIds').value.trim();
    const statusType = document.getElementById('bulkStatusType').value;
    const statusValue = document.getElementById('bulkStatusValue').value;
    if (!rawIds) { toast.error("Please enter order IDs"); return; }
    
    const orderIds = rawIds.split('\n')
      .map(id => id.trim())
      .filter(id => id.length > 0);
    
    setBulkUpdating(true);
    setBulkResult(null);
    try {
      const resp = await axios.post(`${API}/orders/bulk-update-status`, {
        order_ids: orderIds,
        status_type: statusType,
        status_value: statusValue
      });
      setBulkResult(resp.data);
      toast.success(`Updated ${resp.data.updated} orders!`);
    } catch(e) {
      toast.error("Bulk update failed");
    } finally {
      setBulkUpdating(false);
    }
  };

  const BulkUpdateButton = () => (
    <div className="space-y-2">
      <button
        onClick={handleBulkUpdate}
        disabled={bulkUpdating}
        className="w-full bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
      >
        {bulkUpdating ? '⏳ Updating...' : '⚡ Update All'}
      </button>
      {bulkResult && (
        <div className="text-xs p-2 space-y-1">
          <div className="bg-green-50 border border-green-200 rounded-lg p-2">
            <p className="font-semibold text-green-700">✅ Updated: {bulkResult.updated} / {bulkResult.total}</p>
          </div>
          {bulkResult.not_found > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2">
              <p className="font-semibold text-red-700">❌ Not Found: {bulkResult.not_found}</p>
              <div className="mt-1 max-h-32 overflow-y-auto">
                {(bulkResult.not_found_ids || []).map((id, i) => (
                  <p key={i} className="font-mono text-red-600">{id}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
  const [labelChecking, setLabelChecking] = useState(false);
  const [labelCheckTab, setLabelCheckTab] = useState('pdf'); // 'pdf' or 'paste'
  const lastLabelFileRef = useRef(null);
  const [pasteOrderIds, setPasteOrderIds] = useState('');
  const labelCheckRef = useRef(null);

  const handlePasteCheck = async () => {
    const ids = pasteOrderIds.trim().split('\n').map(id => id.trim()).filter(id => id);
    if (!ids.length) { toast.error("Please paste some order IDs"); return; }
    setLabelChecking(true);
    setLabelCheckData(null);
    try {
      const resp = await axios.post(`${API}/analytics/label-check-ids`, { order_ids: ids });
      setLabelCheckData(resp.data);
    } catch(e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLabelChecking(false);
    }
  };

  const handleLabelCheck = async (file) => {
    if (!file) return;
    lastLabelFileRef.current = file;
    setLabelChecking(true);
    setLabelCheckData(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(`${API}/analytics/label-check`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000  // 60 second timeout for large PDFs
      });
      setLabelCheckData(resp.data);
    } catch(e) {
      console.error("Label check error:", e);
      if (e.code === 'ECONNABORTED') {
        toast.error("Request timed out - PDF too large, try splitting it");
      } else {
        toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
      }
    } finally {
      setLabelChecking(false);
    }
  };
  const [customTo, setCustomTo] = useState("");
  const [groupBy, setGroupBy] = useState("hour");

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = { period, group_by: groupBy };
      if (period === "custom" && customFrom && customTo) {
        params.date_from = customFrom;
        params.date_to = customTo;
      }
      const resp = await axios.get(`${API}/analytics/orders`, { params });
      setData(resp.data);
    } catch (e) {
      console.error("Analytics error:", e);
    } finally {
      setLoading(false);
    }
  }, [period, groupBy, customFrom, customTo]);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  const TIME_ORDER = ["🌙 12 AM", "3 AM", "🌅 6 AM", "9 AM", "☀️ 12 PM", "3 PM", "6 PM", "🌆 9 PM", "🌙 11 PM"];
  const STATUS_TIME_ORDER = ["9-10","10-11","11-12","12-1","1-2","2-3","3-4","4-5","5-6","6-7","7-8","8-9"];
  const sortByTime = (a, b) => {
    const ai = TIME_ORDER.indexOf(a[0]);
    const bi = TIME_ORDER.indexOf(b[0]);
    if (ai === -1 && bi === -1) return a[0].localeCompare(b[0]);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  };

  const hourlyChartData = (() => {
    if (!data) return [];
    const existing = data.hourly_data || {};
    return TIME_ORDER.map(slot => ({ time: slot, orders: existing[slot] || 0 }));
  })();

  const statusChartData = (() => {
    if (!data) return [];
    const existing = data.hourly_status || {};
    return STATUS_TIME_ORDER.map(slot => ({ time: slot, ...(existing[slot] || {}) }));
  })();

  const orderStatusChartData = (() => {
    if (!data) return [];
    const existing = data.hourly_order_status || {};
    return STATUS_TIME_ORDER.map(slot => ({ time: slot, ...(existing[slot] || {}) }));
  })();

  const designPieData = data ? Object.entries(data.design_status_counts || {})
    .map(([name, value]) => ({ name, value })) : [];

  const productData = data ? Object.entries(data.product_counts || {})
    .sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([name, value]) => ({ name: name || "Unknown", value })) : [];

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-background">
      <header className="glass-header px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
          <p className="text-sm text-muted-foreground">Real-time order & production insights</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Period Filter */}
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {[["today","Today"],["yesterday","Yesterday"],["week","7 Days"],["month","This Month"],["custom","Custom"]].map(([p, label]) => (
              <Button key={p} size="sm" variant={period === p ? "default" : "ghost"} className="h-7 text-xs" onClick={() => setPeriod(p)}>
                {label}
              </Button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="h-8 text-xs border rounded-md px-2 bg-background"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="h-8 text-xs border rounded-md px-2 bg-background"
              />
              <Button size="sm" className="h-8 text-xs" onClick={fetchAnalytics} disabled={!customFrom || !customTo}>
                Apply
              </Button>
            </div>
          )}
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {[["hour","By Hour"],["day","By Day"],["week","By Week"]].map(([g, label]) => (
              <Button key={g} size="sm" variant={groupBy === g ? "default" : "ghost"} className="h-7 text-xs" onClick={() => setGroupBy(g)}>
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* Stat Cards - Order Status */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Card className="shadow-soft transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{data?.total_orders || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Total Orders</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-emerald-50 border-emerald-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-emerald-700">{data?.order_status_counts?.["DONE"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Order Status</div>
                <div className="text-xs font-semibold text-emerald-600">DONE</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-amber-50 border-amber-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-amber-700">{data?.order_status_counts?.["PENDING"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Order Status</div>
                <div className="text-xs font-semibold text-amber-600">PENDING</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-cyan-50 border-cyan-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-cyan-700">{data?.order_status_counts?.["ENQUIRE"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Order Status</div>
                <div className="text-xs font-semibold text-cyan-600">ENQUIRE</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-red-50 border-red-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-red-700">{data?.order_status_counts?.["CANCEL"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Order Status</div>
                <div className="text-xs font-semibold text-red-600">CANCEL</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-purple-50 border-purple-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-purple-700">{(data?.order_status_counts?.["HALF_ID"] || 0) + (data?.order_status_counts?.["FULL_ID"] || 0)}</div>
                <div className="text-xs text-muted-foreground font-medium">Order Status</div>
                <div className="text-xs font-semibold text-purple-600">HALF+FULL ID</div>
              </CardContent>
            </Card>
          </div>

          {/* Design Status Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Card className="shadow-soft bg-gray-50 border-gray-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-gray-700">{data?.design_status_counts?.["EMPTY"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Design Status</div>
                <div className="text-xs font-semibold text-gray-600">EMPTY</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-yellow-50 border-yellow-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-yellow-700">{data?.design_status_counts?.["PENDING"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Design Status</div>
                <div className="text-xs font-semibold text-yellow-600">PENDING</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-purple-50 border-purple-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-purple-700">{data?.design_status_counts?.["READY"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Design Status</div>
                <div className="text-xs font-semibold text-purple-600">READY</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-emerald-50 border-emerald-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-emerald-700">{data?.design_status_counts?.["DESIGNED"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Design Status</div>
                <div className="text-xs font-semibold text-emerald-600">DESIGNED</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-red-50 border-red-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-red-700">{data?.design_status_counts?.["CANCEL"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Design Status</div>
                <div className="text-xs font-semibold text-red-600">CANCEL</div>
              </CardContent>
            </Card>
            <Card className="shadow-soft bg-blue-50 border-blue-200 transition-all hover:scale-105 hover:shadow-md">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-700">{data?.design_status_counts?.["ENQUIRE"] || 0}</div>
                <div className="text-xs text-muted-foreground font-medium">Design Status</div>
                <div className="text-xs font-semibold text-blue-600">ENQUIRE</div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Orders Over Time */}
        <Card className="shadow-soft">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold tracking-tight">📈 Orders Created Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {hourlyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={hourlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{fontSize: 11, fontFamily: 'Manrope', fontWeight: 600}} />
                  <YAxis tick={{fontSize: 11, fontFamily: 'Manrope'}} />
                  <Tooltip contentStyle={{fontFamily: 'Manrope', fontSize: 12}} />
                  <Line type="monotone" dataKey="orders" stroke="#047857" strokeWidth={2.5} dot={{r: 4, fill: '#047857'}} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">No data for this period</div>
            )}
          </CardContent>
        </Card>

        {/* Order Status Changes */}
        <Card className="shadow-soft">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold tracking-tight">📋 Order Status Changes Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {orderStatusChartData.some(d => Object.keys(d).length > 1) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={orderStatusChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{fontSize: 11, fontFamily: 'Manrope', fontWeight: 600}} />
                  <YAxis tick={{fontSize: 11, fontFamily: 'Manrope'}} allowDecimals={false} />
                  <Tooltip contentStyle={{fontFamily: 'Manrope', fontSize: 12}} />
                  <Legend wrapperStyle={{fontFamily: 'Manrope', fontSize: 12, fontWeight: 600}} />
                  {Object.keys(ORDER_COLORS).map(status => (
                    <Bar key={status} dataKey={status} stackId="a" fill={ORDER_COLORS[status]} radius={[2,2,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">No order status changes in this period</div>
            )}
            {/* Order Status Totals */}
            {data?.order_status_counts && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                {Object.entries(data.order_status_counts).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-1.5 bg-muted rounded-lg px-3 py-1.5">
                    <div className="h-2.5 w-2.5 rounded-full" style={{backgroundColor: ORDER_COLORS[status] || '#94a3b8'}} />
                    <span className="text-xs font-medium">{status}</span>
                    <span className="text-xs font-bold text-primary">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Design Status Changes */}
        <Card className="shadow-soft">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold tracking-tight">📊 Design Status Changes Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={statusChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{fontSize: 11}} />
                  <YAxis tick={{fontSize: 11}} />
                  <Tooltip />
                  <Legend />
                  {Object.keys(DESIGN_COLORS).map(status => (
                    <Bar key={status} dataKey={status} stackId="a" fill={DESIGN_COLORS[status]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">No status changes in this period</div>
            )}
            {/* Design Status Totals */}
            {data?.design_status_counts && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                {Object.entries(data.design_status_counts).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-1.5 bg-muted rounded-lg px-3 py-1.5">
                    <div className="h-2.5 w-2.5 rounded-full" style={{backgroundColor: DESIGN_COLORS[status] || '#94a3b8'}} />
                    <span className="text-xs font-medium">{status}</span>
                    <span className="text-xs font-bold text-primary">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Design Status Pie */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold tracking-tight">🥧 Design Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {designPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={designPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({name, value}) => `${name}:${value}`}>
                      {designPieData.map((entry, i) => (
                        <Cell key={i} fill={DESIGN_COLORS[entry.name] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">No data</div>
              )}
            </CardContent>
          </Card>

          {/* Top Products */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold tracking-tight">📦 Top Products</CardTitle>
            </CardHeader>
            <CardContent>
              {productData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={productData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{fontSize: 10}} />
                    <YAxis dataKey="name" type="category" tick={{fontSize: 10}} width={90} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3b82f6" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">No data</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Order Status */}
        <Card className="shadow-soft">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold tracking-tight">📋 Order ID Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(data?.order_status_counts || {}).map(([status, count]) => (
                <div key={status} className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                  <div className="h-3 w-3 rounded-full" style={{backgroundColor: ORDER_COLORS[status] || '#94a3b8'}} />
                  <span className="text-sm font-medium">{status}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        {/* Label PDF Check */}
        <Card className="shadow-soft">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold tracking-tight">🏷️ Label PDF — Design Status Check</CardTitle>
              {labelCheckData && (
                <button
                  onClick={() => labelCheckTab === 'paste' ? handlePasteCheck() : (lastLabelFileRef.current ? handleLabelCheck(lastLabelFileRef.current) : toast.info('Re-upload the PDF to refresh'))}
                  className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 flex items-center gap-1"
                >
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button onClick={() => { setLabelCheckTab('pdf'); setLabelCheckData(null); }}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${labelCheckTab==='pdf' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600'}`}>
                📄 Upload PDF
              </button>
              <button onClick={() => { setLabelCheckTab('paste'); setLabelCheckData(null); }}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${labelCheckTab==='paste' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-600'}`}>
                📋 Paste Order IDs
              </button>
            </div>

            {labelCheckTab === 'paste' ? (
              <div className="mb-4 space-y-2">
                <textarea
                  value={pasteOrderIds}
                  onChange={e => setPasteOrderIds(e.target.value)}
                  placeholder="Paste order IDs here, one per line&#10;338607328214526080_1&#10;338311007376443264&#10;..."
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  rows={6}
                />
                <button
                  onClick={handlePasteCheck}
                  disabled={labelChecking}
                  className="w-full bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                  {labelChecking ? '⏳ Checking...' : '🔍 Check Design Status'}
                </button>
              </div>
            ) : (
            <div className="mb-4">
              <input ref={labelCheckRef} type="file" accept=".pdf" className="hidden" onChange={e => handleLabelCheck(e.target.files[0])} />
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => labelCheckRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('bg-muted'); }}
                onDragLeave={e => { e.currentTarget.classList.remove('bg-muted'); }}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-muted');
                  const file = e.dataTransfer.files[0];
                  if (file && file.type === 'application/pdf') handleLabelCheck(file);
                  else toast.error("Please drop a PDF file");
                }}
              >
                {labelChecking ? (
                  <><RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin text-muted-foreground" /><p className="text-sm text-muted-foreground">Checking...</p></>
                ) : (
                  <><Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" /><p className="text-sm font-medium">Upload or Drop Label PDF here</p><p className="text-xs text-muted-foreground mt-1">Click to browse or drag & drop</p></>
                )}
              </div>
            </div>
            )}

            {labelCheckData && (
              <div>
                {/* Table with columns */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 overflow-auto">
                  {[
                    {key:"READY", label:"✅ READY", bg:"bg-purple-50", border:"border-purple-200", text:"text-purple-700", head:"text-purple-800"},
                    {key:"PENDING", label:"⏳ PENDING", bg:"bg-yellow-50", border:"border-yellow-200", text:"text-yellow-700", head:"text-yellow-800"},
                    {key:"DESIGNED", label:"🎨 DESIGNED", bg:"bg-emerald-50", border:"border-emerald-200", text:"text-emerald-700", head:"text-emerald-800"},
                    {key:"EMPTY", label:"⬜ EMPTY", bg:"bg-gray-50", border:"border-gray-200", text:"text-gray-700", head:"text-gray-800"},
                    {key:"CANCEL_ENQUIRE", label:"❌ CANCEL+ENQUIRE", bg:"bg-red-50", border:"border-red-200", text:"text-red-700", head:"text-red-800"},
                  ].map(({key, label, bg, border, text, head}) => (
                    <div key={key} className={`rounded-lg border ${border} overflow-hidden`}>
                      <div className={`${bg} px-3 py-2 border-b ${border}`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-xs font-bold ${head} tracking-tight`}>{label}</p>
                          <div className="flex items-center gap-1">
                            <p className={`text-lg font-bold ${text}`}>{(labelCheckData.results[key] || []).length}</p>
                            {(labelCheckData.results[key] || []).length > 0 && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText((labelCheckData.results[key] || []).map(item => typeof item === 'object' ? item.id : item).join('\n'));
                                  toast.success(`Copied all ${(labelCheckData.results[key] || []).length} IDs!`);
                                }}
                                className="ml-1 text-gray-400 hover:text-gray-700"
                                title="Copy all order IDs"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                              </button>
                            )}
                          </div>
                        </div>
                        {(labelCheckData.results[key] || []).some(item => typeof item === 'object' && item.expected !== null) && (
                          <div className="flex gap-1 mt-1">
                            {(() => {
                              const items = labelCheckData.results[key] || [];
                              const greenIds = items.filter(item => typeof item === 'object' && item.expected !== null && item.actual === item.expected).map(item => item.id);
                              const redIds = items.filter(item => typeof item === 'object' && item.expected !== null && item.actual !== item.expected).map(item => item.id);
                              return (<>
                                {greenIds.length > 0 && (
                                  <button
                                    onClick={() => { navigator.clipboard.writeText(greenIds.join('\n')); toast.success(`Copied ${greenIds.length} complete IDs!`); }}
                                    className="text-xs bg-green-100 text-green-700 border border-green-300 rounded px-2 py-0.5 hover:bg-green-200"
                                    title="Copy complete orders (green)"
                                  >🟢 {greenIds.length}</button>
                                )}
                                {redIds.length > 0 && (
                                  <button
                                    onClick={() => { navigator.clipboard.writeText(redIds.join('\n')); toast.success(`Copied ${redIds.length} incomplete IDs!`); }}
                                    className="text-xs bg-red-100 text-red-700 border border-red-300 rounded px-2 py-0.5 hover:bg-red-200"
                                    title="Copy incomplete orders (red)"
                                  >🔴 {redIds.length}</button>
                                )}
                              </>);
                            })()}
                          </div>
                        )}
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {(labelCheckData.results[key] || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-3">None</p>
                        ) : (
                          (labelCheckData.results[key] || []).map((item, i) => {
                            const id = typeof item === 'object' ? item.id : item;
                            const actual = typeof item === 'object' ? item.actual : null;
                            const expected = typeof item === 'object' ? item.expected : null;
                            const phone = typeof item === 'object' ? item.phone_number : null;
                            const isMatch = expected !== null && actual === expected;
                            const isWrong = expected !== null && actual !== expected;
                            return (
                              <div key={i} className={`px-3 py-1.5 border-b ${border} last:border-0 ${bg} flex items-center justify-between`}>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  {phone && (
                                    <a
                                      href={`/dashboard?phone=${phone}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex-shrink-0 text-blue-500 hover:text-blue-700"
                                      title={`Open chat: ${phone}`}
                                    >
                                      💬
                                    </a>
                                  )}
                                  <p className={`text-xs font-mono font-semibold ${text} truncate`}>{id}</p>
                                </div>
                                {actual !== null && actual !== undefined && (
                                  <span className={`text-xs font-bold ml-2 flex-shrink-0 ${isMatch ? 'text-green-600' : isWrong ? 'text-red-600' : 'text-gray-500'}`}>
                                    {actual}{expected !== null ? `/${expected}` : ''}
                                  </span>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      {/* Bulk Status Update */}
      <div className="mt-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold tracking-tight">⚡ Bulk Status Update</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1 space-y-3">
                <div>
                  <label className="text-sm font-medium">Status Type</label>
                  <select id="bulkStatusType" className="w-full mt-1 border rounded-lg px-3 py-2 text-sm" value={bulkStatusType} onChange={e => setBulkStatusType(e.target.value)}>
                    <option value="order_status">Order Status</option>
                    <option value="design_status">Design Status</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">New Value</label>
                  <select id="bulkStatusValue" className="w-full mt-1 border rounded-lg px-3 py-2 text-sm">
                    {bulkStatusType === 'order_status' ? <>
                      <option value="DONE">Done</option>
                      <option value="PENDING">Pending</option>
                      <option value="HOLD">Hold</option>
                      <option value="CHECK">Check</option>
                      <option value="CANCEL">Cancel</option>
                      <option value="ENQUIRE">Enquire</option>
                      <option value="HALF_ID">Half ID</option>
                      <option value="FULL_ID">Full ID</option>
                    </> : <>
                      <option value="EMPTY">Empty</option>
                      <option value="PENDING">Pending</option>
                      <option value="READY">Ready</option>
                      <option value="DESIGNED">Designed</option>
                      <option value="CANCEL">Cancel</option>
                      <option value="ENQUIRE">Enquire</option>
                    </>}
                  </select>
                </div>
                <BulkUpdateButton />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Order IDs (one per line, with or without _1)</label>
                <textarea
                  id="bulkOrderIds"
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm font-mono"
                  rows={10}
                  placeholder="338607328214526080_1&#10;338311007376443264&#10;338778495012528256_1&#10;..."
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}
